"use client";

import { get_search_products_query } from "@core/services/products";
import { useMenusContext } from "../contexts/MenusContext";
import { is_function } from "@core/helpers/function";
import useSearchDebounce from "./useSearchDebounce";
import { useCallback, useEffect, useMemo, useReducer, useState } from "react";
import {
  convert_to_en_number,
  convert_to_en_number_string,
} from "@core/helpers/string";
import { get_search_blogs_query } from "@core/services/blogs";
import {
  get_cookie_client,
  set_cookie_client,
} from "@core/helpers/client/cookie";
const search_reducer = (state, { type, payload }) => {
  switch (type) {
    case "GET_DATA_PENDING":
      return { ...state, search_loading: true };
    case "GET_DATA_END":
      return { ...state, search_loading: false };
    case "GET_DATA_SUCCESS": {
      const { products, blogs, brands, categories } = payload;
      return {
        ...state,
        products,
        blogs,
        brands,
        categories,
      };
    }
    case "SET_SEARCH_HISTORY":
      return { ...state, search_history: payload };
    default:
      return state;
  }
};

const read_search_history_cookie = () => {
  try {
    const parsed = JSON.parse(get_cookie_client("search-history") || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const useSearch = ({
  model_name = "ProductModel",
  limit = 5,
  blog_card_mockup,
  category_card_mockup,
  model_type = 1,
  category_type = 3
} = {}) => {
  const { menus } = useMenusContext();
  const [page, set_page] = useState(1);
  const [search, set_search] = useState("");
  const [delayed_search, set_delayed_search] = useSearchDebounce("", 350);
  const [state, dispatch] = useReducer(search_reducer, {
    products: null,
    blogs: null,
    brands: null,
    categories: null,
    search_loading: false,
    search_history: [],
  });

  // کوکی فقط روی کلاینت در دسترس است. اگر همان رندر اول خوانده شود، html سرور
  // (که تاریخچه‌ای ندارد) با اولین رندر کلاینت فرق می‌کند و هیدریشن fail می‌شود.
  // پس رندر اول همیشه خالی است و تاریخچه در افکت بعد از mount ست می‌شود.
  useEffect(() => {
    const history = read_search_history_cookie();
    if (history?.length) {
      dispatch({ type: "SET_SEARCH_HISTORY", payload: history });
    }
  }, []);

  const search_on_change = useCallback((e) => {
    const { value } = e?.target;
    set_search(value);
    set_delayed_search(value);
  }, []);

  const set_search_history = useCallback(
    (data) => {
      const search_history_cookie = read_search_history_cookie();
      const check_item = search_history_cookie?.find(
        (item) => item?.link === data?.link
      );

      let new_search_history = [];
      if (check_item) {
        new_search_history = search_history_cookie?.filter(
          (item) => JSON.stringify(item) !== JSON.stringify(check_item)
        );
      } else {
        new_search_history = [...(search_history_cookie || []), data];
        if (new_search_history?.length > 5) {
          new_search_history = new_search_history?.filter(
            (_, index) => index !== 0
          );
        }
      }

      set_cookie_client(
        "search-history",
        JSON.stringify(new_search_history || [])
      );
      dispatch({ type: "SET_SEARCH_HISTORY", payload: new_search_history });
    },
    [state?.search_history]
  );

  const popular_searches = useMemo(() => menus?.["popular-searches"], [menus]);

  const get_search_products = useCallback(
    async (args = {}) => {
      const { success_callback, error_callback } = args;
      dispatch({ type: "GET_DATA_PENDING" });

      let result;
      if (model_name == "BlogModel") {
        result = await get_search_blogs_query(
          {
            page: page,
            limit: limit,
            search: convert_to_en_number_string(delayed_search?.trim()),
          },
          {
            blog_card_mockup,
            category_card_mockup,
          }
        );
      } else {
        result = await get_search_products_query({
          page: page,
          limit: limit,
          sort_by_in_stock: true,
          search: convert_to_en_number_string(delayed_search?.trim()),
          product_type: model_type,
          category_type,
        });
      }
      if (result?.status) {
        dispatch({ type: "GET_DATA_SUCCESS", payload: result?.data });
        if (is_function(success_callback)) {
          success_callback(result);
        }
      } else {
        if (is_function(error_callback)) {
          error_callback(result);
        }
      }
      dispatch({ type: "GET_DATA_END" });
    },
    [delayed_search]
  );

  useEffect(() => {
    if (delayed_search?.length >= 2) {
      get_search_products();
    }
  }, [delayed_search]);

  const delete_all_search_history = () => {
    set_cookie_client(
      "search-history",
      JSON.stringify([])
    );
    dispatch({ type: "SET_SEARCH_HISTORY", payload: [] });
  }

  return {
    search,
    set_search,
    search_on_change,
    popular_searches,
    state,
    set_search_history,
    page,
    set_page,
    delete_all_search_history
  };
};

export default useSearch;
